import React, { FC, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DialogOverlay, DialogContent, CloseButton } from './VideoDialog.styles';

export interface VideoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
}

export const VideoDialog: FC<VideoDialogProps> = ({ isOpen, onClose, videoUrl }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      document.body.style.overflow = 'hidden';
    } else if (!isOpen && isMounted) {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, isMounted]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsMounted(false);
      onClose();
    }, 300); // 300ms matches the CSS animation duration
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isMounted || typeof document === 'undefined') return null;

  return createPortal(
    <DialogOverlay $isClosing={isClosing} onClick={handleClose}>
      <CloseButton onClick={handleClose} aria-label="Close dialog">
        &times;
      </CloseButton>
      <DialogContent onClick={e => e.stopPropagation()}>
        <iframe src={videoUrl} allow="autoplay; fullscreen" allowFullScreen title="Video Dialog"></iframe>
      </DialogContent>
    </DialogOverlay>,
    document.body
  );
};
